import { MICRO_SECONDS_IN_SECOND } from "../utils/time";

export type TaskType = "video-to-shorts" | "video-to-video";

/**
 * Stuck-task timeout ladder. Keep aligned with klap-api
 * `EXPIRED_TASK_TIMEOUT_HOURS` in `expired-tasks-collector.ts`.
 *
 * Soft warning (FE only): reassure the user, keep showing progress.
 * Backend collector: authoritative `error` + `timeout-collected`.
 * Hard FE dead-man: only after the collector should have run, so the UI
 * never shows Failed while the task is still live on the server.
 */
export const STUCK_TASK_STALL_WARNING_HOURS = 1.5;
/**
 * Client-side hard failure for tasks still "processing" with no status update.
 * Must stay strictly greater than the backend collector
 * (`EXPIRED_TASK_TIMEOUT_HOURS` = 2.2 in klap-api).
 */
export const STUCK_TASK_TIMEOUT_HOURS = 2.5;

export type TopicAuxDatum = AuxDatum & { type: "topic"; data: string };
export type AuxDatum = {
  type: string;
  data: any;
  start: number;
  end: number;
};

// Suggested-moment shape, kept for the clips-timeline overlay
// (VideoClipsTimeline / MomentCard). Moments are no longer sourced from the
// task row — curated moments are now persistent draft projects (videos_v2,
// status "draft"); see hooks/useFolder.ts (`drafts`).
export type TaskMoment = {
  id: string;
  topic: string;
  description: string;
  hookTitle: string;
  viralityScore: number;
  viralityScoreExplanation: string;
  /** Cut ranges in microseconds (stored as seconds in the DB). */
  rangesUs: [number, number][];
  /** Overall span of the moment in the source video (microseconds). */
  startUs: number;
  endUs: number;
};

export type TaskModel = {
  id: string;
  type: TaskType;
  status: "processing" | "ready" | "error" | "expired";
  errorCode: string;
  estimatedTimeSeconds: number;
  createdAt: Date;
  outputType: "project" | "folder";
  outputId: string;
  srcName: string;
  srcUrl: string;
  srcDuration: number;
  sourceMediaId?: string;
  auxiliaryData: AuxDatum[];
  presetId?: string;
  isFallback: boolean;
};

export const serializeTask = (task: TaskModel) => {
  return {
    id: task.id,
    type: task.type,
    status: task.status,
    error_code: task.errorCode,
    estimated_time_seconds: task.estimatedTimeSeconds,
    created_at: task.createdAt,
    output_type: task.outputType,
    output_id: task.outputId,
    preset_id: task.presetId,
    is_fallback: task.isFallback,
  };
};

export const deserializeTask = (task: any): TaskModel => {
  return {
    id: task.id,
    type: task.type,
    status: task.status,
    errorCode: task.error_code,
    estimatedTimeSeconds: task.estimated_time_seconds,
    createdAt: new Date(task.created_at),
    outputType: task.output_type,
    outputId: task.output_id,
    srcUrl: task.src_url,
    srcName: task.src_name,
    srcDuration: task.src_duration_us
      ? task.src_duration_us
      : Math.round(task.src_duration_seconds * MICRO_SECONDS_IN_SECOND),
    sourceMediaId: task.source_media_id,
    auxiliaryData: task.auxiliary_data,
    presetId: task.preset_id,
    isFallback: task.is_fallback,
  };
};
